"use client";

import { Icon } from "@plura/client/components";
import type { IconName } from "@plura/shared/icon-glyphs";
import { cn } from "@plura/shared/utils";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../ui/Button";

/**
 * Generic comparison table — shared by the pricing page (3 plans, teal accent)
 * and the Typeform page (Formsuite vs Typeform, brown accent). Pages map their
 * data into {@link CompareColumn}s + {@link CompareSection}s; cells are arbitrary
 * nodes so each page controls its own cell rendering.
 */
export interface CompareColumn {
  id: string;
  title: string;
  subtitle?: string;
  /** Optional `$price/mo` shown in the header + compact bar. */
  price?: number;
  /** Small note under the price (e.g. "for 100 responses"). */
  priceNote?: string;
  featured?: boolean;
  /** Optional logo node rendered before the title (e.g. the Formsuite mark). */
  logo?: React.ReactNode;
  /** Optional header CTA button. */
  headerCta?: { label: React.ReactNode; href: string };
}

export interface CompareRow {
  label: string;
  /** One node per column, in column order. */
  cells: React.ReactNode[];
}

export interface CompareSection {
  id: string;
  /** Omit for an un-headered section (flat rows). */
  title?: string;
  icon?: IconName;
  rows: CompareRow[];
}

type Accent = "green" | "primary";

const ACCENT: Record<Accent, { solid: string; tint: string; ctaSolid: string }> = {
  green: { solid: "bg-green", tint: "bg-green/[0.04]", ctaSolid: "border-green bg-green" },
  primary: { solid: "bg-primary", tint: "bg-primary/[0.05]", ctaSolid: "border-primary bg-primary" },
};

interface Props {
  title?: string;
  /** Top-left label above the row labels (e.g. "Plans & Pricing", "Feature"). */
  leadLabel: string;
  columns: CompareColumn[];
  sections: CompareSection[];
  accent?: Accent;
  /** Optional single CTA repeated under every column. */
  footerCta?: { label: React.ReactNode; href: string };
  /** Remove the gap between the lead column and the comparison columns. */
  tightLead?: boolean;
}

// Matches md:top-[72px] on the compact bar (sticky bar is desktop-only).
const PIN_LINE_PX = 72;

const CompareTable = ({ title, leadLabel, columns, sections, accent = "green", footerCta, tightLead }: Props) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(false);

  const a = ACCENT[accent];
  const colsClass = columns.length === 2 ? "grid-cols-2" : "grid-cols-3";
  const hasPricing = columns.some((c) => c.price !== undefined);
  // Headered sections get breathing room; flat (un-titled) tables sit flush.
  const headered = sections.some((s) => s.title);

  // Lead label column + N equal columns. By default a gap sits between the lead
  // and the column group; `tightLead` makes them flush.
  const ROW_GRID = cn(
    "grid grid-cols-[160px_1fr] md:grid-cols-[285px_1fr]",
    !tightLead && "gap-x-4 md:gap-x-[21px]",
  );

  useEffect(() => {
    const node = headerRef.current;
    if (!node) return;
    const update = () => {
      const { top, bottom } = node.getBoundingClientRect();
      setPinned(bottom <= PIN_LINE_PX && top < window.innerHeight);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div className="w-full">
      {title && (
        <h2 className="text-h2-medium font-heading mb-[60px] text-center text-dark max-md:mb-10">{title}</h2>
      )}

      {/* Too many columns for a phone — scroll horizontally at a comfortable min-width. */}
      <div className="max-md:-mx-5 max-md:overflow-x-auto max-md:px-5 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden">
        <div className="max-md:min-w-[600px]">
          {/* Full header — static in flow */}
          <div ref={headerRef} className={ROW_GRID}>
            <div className="flex items-start pt-6">
              <span className="font-body text-xl font-medium tracking-[-0.02em] text-dark">{leadLabel}</span>
            </div>
            <div className={cn("grid", colsClass)}>
              {columns.map((col, i) => (
                <HeaderCell
                  key={col.id}
                  column={col}
                  first={i === 0}
                  last={i === columns.length - 1}
                  accent={a}
                  hasPricing={hasPricing}
                />
              ))}
            </div>
          </div>

          {/* Compact pinned bar — zero-height sticky wrapper, tucks under the site header */}
          <div className="pointer-events-none sticky top-[64px] z-[5] h-0 max-md:hidden md:top-[72px]">
            <div
              className={cn(
                "absolute left-1/2 top-0 w-screen -translate-x-1/2 border-b border-[#e5e5e5] bg-canvas/90 backdrop-blur-md transition-all duration-300 ease-out",
                "shadow-[0_8px_20px_rgba(18,18,18,0.06)]",
                pinned
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : "pointer-events-none -translate-y-1 opacity-0",
              )}
            >
              <div className={cn(ROW_GRID, "landing-container items-stretch")}>
                <div className="flex items-center">
                  <span className="font-body text-sm font-semibold tracking-[-0.02em] text-dark">{leadLabel}</span>
                </div>
                <div className={cn("grid overflow-hidden rounded-t-lg", colsClass)}>
                  {columns.map((col, i) => (
                    <div
                      key={col.id}
                      className={cn(
                        "flex flex-col items-center justify-center gap-0.5 px-2 py-3 text-center",
                        col.featured ? cn(a.solid, "text-white") : "text-dark",
                        !col.featured && i === 0 && "rounded-l-lg",
                        !col.featured && i === columns.length - 1 && "rounded-r-lg",
                      )}
                    >
                      <span className="font-body text-sm font-bold leading-tight">{col.title}</span>
                      {col.price !== undefined && (
                        <span
                          className={cn(
                            "font-body text-xs font-medium capitalize leading-tight",
                            col.featured ? "text-white/85" : "text-[#555555]",
                          )}
                        >
                          ${col.price}/mo
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className={cn("flex flex-col", headered ? "mt-[33px] gap-[38px]" : "mt-0")}>
            {sections.map((section) => (
              <div key={section.id} className="flex flex-col">
                {section.title && (
                  <div className="bg-shade flex items-center gap-2.5 border-b border-[#e5e5e5] px-5 py-2.5">
                    {section.icon && (
                      <Icon name={section.icon} className="!size-5 shrink-0 fill-dark text-dark" aria-hidden />
                    )}
                    <h3 className="font-body text-lg font-bold text-dark">{section.title}</h3>
                  </div>
                )}

                {section.rows.map((row, ri) => (
                  <div key={ri} className={ROW_GRID}>
                    <div className="flex items-center border-b border-[#e5e5e5] py-[22px] pl-5 max-md:py-4 max-md:pl-1">
                      <span className="font-body text-base font-medium leading-6 text-[#313131] max-md:text-sm">
                        {row.label}
                      </span>
                    </div>
                    <div className={cn("grid", colsClass)}>
                      {columns.map((col, ci) => (
                        <div
                          key={col.id}
                          className={cn(
                            "flex items-center justify-center border-b border-[#e5e5e5] px-2.5 py-[22px] text-center max-md:px-1 max-md:py-4",
                            col.featured && a.tint,
                          )}
                        >
                          {row.cells[ci]}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Footer CTAs — one per column */}
          {footerCta && (
            <div className={ROW_GRID}>
              <div aria-hidden className="max-md:hidden" />
              <div className={cn("grid", colsClass)}>
                {columns.map((col) => (
                  <div key={col.id} className="flex items-center justify-center px-2.5 pt-8 max-md:px-1 max-md:pt-6">
                    <Button
                      href={footerCta.href}
                      variant="secondary"
                      size="medium"
                      className={cn(col.featured && cn(a.ctaSolid, "text-white hover:opacity-90"))}
                    >
                      {footerCta.label}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface HeaderCellProps {
  column: CompareColumn;
  first: boolean;
  last: boolean;
  accent: { solid: string; tint: string; ctaSolid: string };
  hasPricing: boolean;
}

const HeaderCell = ({ column, first, last, accent, hasPricing }: HeaderCellProps) => {
  const { title, subtitle, price, priceNote, featured, logo, headerCta } = column;
  const hasFooter = price !== undefined || !!headerCta;

  return (
    <div
      className={cn(
        "flex flex-col justify-between gap-7 px-5 py-6 max-md:px-3",
        hasPricing ? "min-h-[240px]" : "min-h-[150px]",
        featured ? cn(accent.solid, "text-white") : "bg-white",
        !featured && first && "rounded-l-xl border border-r-0 border-[#e5e5e5]",
        !featured && last && "rounded-r-xl border border-l-0 border-[#e5e5e5]",
        !featured && !first && !last && "border-y border-[#e5e5e5]",
      )}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          {logo && <span className="flex size-[22px] shrink-0 items-center justify-center [&_svg]:block [&_svg]:size-full">{logo}</span>}
          <span className="font-body text-lg font-bold leading-tight">{title}</span>
        </div>
        {subtitle && (
          <p className={cn("font-body text-sm leading-[22px]", featured ? "text-white/90" : "text-[#313131]")}>
            {subtitle}
          </p>
        )}
      </div>

      {hasFooter && (
        <div className="flex flex-col gap-4">
          {price !== undefined && (
            <div className="flex flex-col gap-0.5">
              <span className="font-body text-lg font-bold capitalize leading-tight">
                ${price}
                <span className={cn("ml-1 text-sm font-medium", featured ? "text-white/80" : "text-[#555555]")}>/mo</span>
              </span>
              {priceNote && (
                <span className={cn("font-body text-xs font-medium leading-tight", featured ? "text-white/70" : "text-[#777777]")}>
                  {priceNote}
                </span>
              )}
            </div>
          )}
          {headerCta && (
            <Button href={headerCta.href} variant={featured ? "white" : "secondary"} size="medium" className="w-full">
              {headerCta.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CompareTable;
